import { Invoice } from "@lib/types/Billing";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGetInvoice, apiUpdateInvoice } from "@lib/api/billing";
import InvoiceSubmitButton from "./InvoiceSubmitButton";
import InvoiceInfo from "./InvoiceInfo";
import InvoiceItems from "./InvoiceItems";
import { apiGetProducts } from "@lib/api/product";
import { useInvoice } from "./useInvoice";
import { Customer } from "@lib/types/Customer";
import InvoicePriceSummary from "./InvoicePriceSummary";
import { toast } from "react-toastify";
import { FormattedMessage, useIntl } from "react-intl";
import { StyledInvoice, StyledInvoiceBody } from "./index.styled";
import InvoiceFooter from "./InvoiceFooter";
import { useApi } from "@lib/hooks/APIHooks";

type Props = {
  open: boolean;
  invoice?: Invoice;
  setSelectedInvoice: (invoice?: Invoice) => void;
  invoiceListQueryKey: string[];
  customer?: Customer;
  tabToBeOpenedOnFooter?: string;
  isEditMode: boolean;
};

export default function InvoiceComponent(props: Props) {
  const {
    open = true,
    invoice,
    setSelectedInvoice,
    invoiceListQueryKey,
    customer,
    tabToBeOpenedOnFooter,
    isEditMode,
  } = props;
  const { messages } = useIntl();
  const { queriesCacheClear } = useApi();

  const invoiceQuery = useQuery({
    queryFn: () =>
      apiGetInvoice(invoice?.id, {
        join: ["customer", "orders", "orders.product"],
      }),
    queryKey: ["invoice-detail", invoice?.id || "new"],
    enabled: open && !!invoice?.id,
    staleTime: 1000 * 60,
  });
  const productsQuery = useQuery({
    queryFn: () => apiGetProducts({ join: ["customer"] }),
    queryKey: ["products"],
    enabled: open && isEditMode,
  });
  const queryClient = useQueryClient();
  const updateInvoiceQuery = useMutation({
    mutationFn: apiUpdateInvoice,
    mutationKey: [...invoiceListQueryKey],
    onSuccess() {
      toast.success(
        <FormattedMessage
          id="common.updatedMessage"
          values={{ name: messages["common.invoice"] as string }}
        />,
      );
      queriesCacheClear({ search: "invoice" });
      queryClient.invalidateQueries({
        queryKey: [...invoiceListQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: ["invoice-detail"],
      });
    },
  });

  const {
    items,
    requestBody,
    invoiceData,
    itemsTotalAmount,
    itemsTotalDiscount,
    itemsTotalWithoutDiscountAmount,
    onChangeLineItems,
    onDeleteLineItem,
    handleRequestBody,
    setItems,
    handleCloneInvoice,
  } = useInvoice({
    invoiceQuery,
    productsQuery,
    setSelectedInvoice,
    customer,
  });

  return (
    <StyledInvoice
      id="invoice"
      // className="print-process"
    >
      <StyledInvoiceBody>
        <InvoiceInfo
          invoice={invoiceData}
          requestBody={requestBody}
          handleRequestBody={handleRequestBody}
          customer={customer}
          updateQuery={updateInvoiceQuery}
          isEditMode={isEditMode}
        />

        <InvoiceItems
          items={items}
          onChangeLineItems={onChangeLineItems}
          onDeleteLineItem={onDeleteLineItem}
          loading={invoiceQuery?.isLoading}
          invoiceProducts={invoiceQuery?.data?.data?.orders?.map(
            (order) => order.product,
          )}
          productsQuery={productsQuery}
          isEditMode={isEditMode}
        />

        <InvoicePriceSummary
          isEditMode={isEditMode}
          setItems={setItems}
          items={items}
          invoice={invoiceQuery?.data?.data}
          itemsTotalAmount={itemsTotalAmount}
          itemsTotalDiscount={itemsTotalDiscount}
          itemsTotalWithoutDiscountAmount={itemsTotalWithoutDiscountAmount}
        />

        <InvoiceSubmitButton
          invoice={invoiceQuery?.data?.data}
          setSelectedInvoice={setSelectedInvoice}
          requestBody={requestBody}
          invoiceListQueryKey={invoiceListQueryKey}
          handleCloneInvoice={handleCloneInvoice}
          updateQuery={updateInvoiceQuery}
          isEditMode={isEditMode}
        />
      </StyledInvoiceBody>

      {isEditMode && (
        <InvoiceFooter
          invoice={invoiceQuery?.data?.data}
          invoiceQueryKey={invoiceListQueryKey}
          openTab={tabToBeOpenedOnFooter}
        />
      )}
    </StyledInvoice>
  );
}
